import type { FormOption } from '@/config/collectionsLanding';

interface FieldShellProps {
  label: string;
  htmlFor?: string;
  error?: string;
  optional?: boolean;
  helper?: string;
  children: React.ReactNode;
}

export function FieldShell({ label, htmlFor, error, optional, helper, children }: FieldShellProps) {
  const errId = htmlFor ? `${htmlFor}-error` : undefined;
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-base font-semibold text-ink-900">
        {label}
        {optional && <span className="mr-1 text-sm font-normal text-ink-400"> (לא חובה)</span>}
      </label>
      {helper && <p className="mt-1 text-sm text-ink-500">{helper}</p>}
      <div className="mt-3">{children}</div>
      {error && (
        <p id={errId} role="alert" className="mt-2 text-sm font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const optionBase =
  'flex w-full items-center gap-2 rounded-xl border px-4 py-3 text-right text-base transition cursor-pointer select-none';

interface RadioGroupProps {
  name: string;
  options: FormOption[];
  value: string;
  onChange: (value: string) => void;
}

export function RadioGroup({ name, options, value, onChange }: RadioGroupProps) {
  return (
    <div role="radiogroup" className="grid gap-2.5 sm:grid-cols-2">
      {options.map((opt) => {
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            className={`${optionBase} ${
              checked
                ? 'border-brand-600 bg-brand-50 font-semibold text-brand-800 ring-1 ring-brand-600'
                : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => onChange(opt.value)}
              className="h-4 w-4 accent-brand-600"
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

interface CheckboxGroupProps {
  options: FormOption[];
  values: string[];
  onToggle: (value: string) => void;
}

export function CheckboxGroup({ options, values, onToggle }: CheckboxGroupProps) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {options.map((opt) => {
        const checked = values.includes(opt.value);
        return (
          <label
            key={opt.value}
            className={`${optionBase} ${
              checked
                ? 'border-brand-600 bg-brand-50 font-semibold text-brand-800 ring-1 ring-brand-600'
                : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300'
            }`}
          >
            <input
              type="checkbox"
              value={opt.value}
              checked={checked}
              onChange={() => onToggle(opt.value)}
              className="h-4 w-4 accent-brand-600"
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'email' | 'tel';
  autoComplete?: string;
  error?: string;
  optional?: boolean;
  helper?: string;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  autoComplete,
  error,
  optional,
  helper,
}: TextFieldProps) {
  return (
    <FieldShell label={label} htmlFor={id} error={error} optional={optional} helper={helper}>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-ink-900 outline-none transition placeholder:text-ink-400 ${
          error
            ? 'border-red-400 focus:border-red-500'
            : 'border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
        }`}
      />
    </FieldShell>
  );
}

export function ProgressBar({ labels, current }: { labels: string[]; current: number }) {
  return (
    <div className="mb-6">
      <ol className="flex items-center gap-2">
        {labels.map((label, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={label} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex w-full items-center">
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold transition ${
                    done || active ? 'bg-brand-600 text-white' : 'bg-slate-200 text-ink-500'
                  }`}
                >
                  {i + 1}
                </span>
                {i < labels.length - 1 && (
                  <span
                    className={`h-1 flex-1 rounded-full ${done ? 'bg-brand-600' : 'bg-slate-200'}`}
                  />
                )}
              </div>
              <span
                className={`text-xs font-medium ${active ? 'text-brand-700' : 'text-ink-500'}`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
